import React, { useContext, useState, useEffect } from "react";
import { SurveyContext, QuestionProps } from "../../survey";
import Checkbox from "../../../components/data_entry/checkbox";
import Input from "../../../components/data_entry/input";
import { RadioGroup, RadioButton } from "../../../components/data_entry/radio";
import { MentorChatGroupsDocument } from "../../../generated/graphql";
import styled from "styled-components";
import QuestionLabel from "../components/question_label";
import nextId from "react-id-generator";
import { useQuery } from "urql";
import { captureError } from "../../../../utils/capture_error";

type answer = false | { studentId: string; info: string }[];

const StyledStudentSelect = styled.div`
  margin-top: 12px;
`;

export default function MentorChatGroupOptionalStudentInfo({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
}: QuestionProps<answer, { yesNoPrompt: string; selectPrompt: string }>) {
  const { participantId, mentorshipChatGroupId } = useContext(SurveyContext);

  // this is a temporary data store for input student info. when the user un-checks
  // a student, the info gets removed from currentAnswer. We want to make sure they
  // don't have to re-type everything if they re-check the student again, so we save
  // it here just in case
  const [savedStudentInfo, setSavedStudentInfo] = useState<{
    [studentId: string]: string;
  }>({});

  const [{ fetching, error, data }] = useQuery({
    query: MentorChatGroupsDocument,
    variables: { mentorId: participantId },
  });

  const answerIsValid = typeof currentAnswer !== "undefined";
  useEffect(() => {
    setAbleToSubmit(answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  if (fetching || data === null) return <div>Loading...</div>;
  if (error || !data) {
    const graphQLErrorsForAPM = error?.graphQLErrors?.join("; ") ?? "";
    captureError(new Error(`Error fetching useMentorChatGroupsQuery: ${graphQLErrorsForAPM}`));
    return null;
  }

  const mentorships = data.mentor.mentorships;

  if (!mentorshipChatGroupId) {
    throw new Error("Can only use as part of a mentorship group survey");
  }
  const mentorshipsInChatGroup = mentorships.filter(
    (mentorship) => mentorship.chatGroup && mentorship.chatGroup.id === mentorshipChatGroupId,
  );

  const yesNoLabel = customizations.yesNoPrompt;
  const selectLabel = customizations.selectPrompt;
  const id = nextId();

  return (
    <div className="optional-student-info">
      <QuestionLabel label={yesNoLabel} htmlFor={id} />
      <RadioGroup
        name="recordStudentInfo"
        value={
          // if the user hasn't provided an answer yet, don't select anything
          typeof currentAnswer === "undefined" ? "" : currentAnswer ? "yes" : "no"
        }
        onChange={(newValue) => updateAnswer(newValue === "yes" ? [] : false)}
      >
        <RadioButton
          data-test="optional-student-info-yes"
          key="yes"
          label="Yes"
          value="yes"
          htmlFor={id}
        />
        <RadioButton key="no" label="No" value="no" htmlFor={id} />
      </RadioGroup>
      {currentAnswer && (
        <StyledStudentSelect>
          <QuestionLabel label={selectLabel} htmlFor={id} />
          {mentorshipsInChatGroup.map((mentorship) => {
            const student = mentorship.student;
            const user = student.user;

            const answerIndex = currentAnswer.findIndex(
              (answer) => answer.studentId === student.id,
            );
            return (
              <div key={student.id}>
                <Checkbox
                  dataTest="optional-student-info-student-select"
                  label={`${user.firstName} ${user.lastName}`}
                  checked={currentAnswer.map((student) => student.studentId).includes(student.id)}
                  htmlFor={id}
                  onChange={() => {
                    if (answerIndex > -1) {
                      updateAnswer([
                        ...currentAnswer.slice(0, answerIndex),
                        ...currentAnswer.slice(answerIndex + 1),
                      ]);
                    } else {
                      updateAnswer([
                        ...currentAnswer,
                        {
                          studentId: student.id,
                          info: savedStudentInfo[student.id] ? savedStudentInfo[student.id] : "",
                        },
                      ]);
                    }
                  }}
                />
                {answerIndex > -1 && (
                  <Input
                    data-test="optional-student-info-text"
                    type="text"
                    id={id}
                    value={currentAnswer[answerIndex].info}
                    onChange={({ currentTarget: { value: newInfo } }) => {
                      setSavedStudentInfo({
                        ...savedStudentInfo,
                        [student.id]: newInfo,
                      });
                      updateAnswer([
                        ...currentAnswer.slice(0, answerIndex),
                        { ...currentAnswer[answerIndex], info: newInfo },
                        ...currentAnswer.slice(answerIndex + 1),
                      ]);
                    }}
                  />
                )}
              </div>
            );
          })}
        </StyledStudentSelect>
      )}
    </div>
  );
}
